import { T, useT } from '@transifex/react';
import { Avatar, AvatarImage, Checkbox, Text, VStack } from '@vidahealth/vida-ui';
import { useCallback, useState } from 'react';

import { formatBirthdayForDisplay } from '@/modules/localization';
import {
  CheckboxCheckIndicator,
  ContactSupport,
  CtaButton,
  Screen,
  ScreenHeading,
  SelectableRow,
  TextField,
} from '@/modules/ui';

import { useSurveyQuestions } from '../../hooks/useSurveyQuestions';
import type { Answers, Conditions, SurveyQuestion } from '../../types/surveyQuestion';
import { validateSurveyDate } from '../../utils/validateSurveyDate';

type SurveyContentProps = {
  questions: SurveyQuestion[];
  conditions?: Conditions;
  /** Called with the full answer map once the group's last question is answered. */
  onContinue: (answers: Answers) => void;
  isSubmitting?: boolean;
  /**
   * Per-survey testID prefix (e.g. `phq-gad`). Derives stable selectors for the
   * Continue button, the date input, and each answer row — keyed by the active
   * question so option values that repeat across questions (PHQ/GAD scales) stay
   * unique. Omitted → no testIDs.
   */
  testIDPrefix?: string;
};

// Human-friendly testID segment derived from a choice's display label:
// lowercased, non-alphanumeric runs collapsed to single hyphens, trimmed
// (e.g. "More than half the days" → "more-than-half-the-days", "Female" →
// "female"). Only one question renders at a time, so a label-based id is unique
// in the tree even when answer labels repeat across questions (PHQ/GAD scales).
const slugify = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// RN port of stylos's SurveyPage: shows one question at a time. Unlike Sparta
// (which auto-advances on choice tap), selecting a choice only highlights it —
// the user advances with an explicit Continue button, for consistency with the
// rest of the onboarding flow. 'date' questions use a masked input. When the
// last question is answered, onContinue fires.
export function SurveyContent({
  questions,
  conditions,
  onContinue,
  isSubmitting = false,
  testIDPrefix,
}: SurveyContentProps) {
  const t = useT();
  const [answers, setAnswers] = useState<Answers>({});
  // Holds the pending selection for the active question — one entry for
  // single-select (radiogroup/scale), many for multi-select (checkbox).
  const [selected, setSelected] = useState<string[]>([]);
  const [dateValue, setDateValue] = useState('');
  // Holds the full date error message (with the concrete min date baked in), or
  // null when valid — so the min only lives in one place: the message we set.
  const [dateError, setDateError] = useState<string | null>(null);
  const [showRequiredError, setShowRequiredError] = useState(false);

  const { activeQuestion, setNextActiveQuestion } = useSurveyQuestions(questions, conditions);

  const isMultiSelect = activeQuestion?.isMultiSelect === true;

  // Commit the answer for the active question, reset the pending selection, and
  // advance (or finish the group). A `string[]` value comes from a multi-select
  // (checkbox) question; conditions never target multi-select, so `Answer`'s
  // string `questionValue` gets '' in that case (no stop/jump can match).
  const commitAnswer = useCallback(
    (value: string | string[]) => {
      if (!activeQuestion) return;
      const nextAnswers = { ...answers, [activeQuestion.key]: value };
      setAnswers(nextAnswers);
      setSelected([]);
      setDateValue('');
      setDateError(null);
      setShowRequiredError(false);
      setNextActiveQuestion(
        { questionKey: activeQuestion.key, questionValue: Array.isArray(value) ? '' : value },
        () => onContinue(nextAnswers)
      );
    },
    [activeQuestion, answers, setNextActiveQuestion, onContinue]
  );

  // Toggle a choice: multi-select adds/removes it from the set; single-select
  // replaces the whole selection with just this value.
  const toggleChoice = useCallback(
    (value: string) => {
      setShowRequiredError(false);
      setSelected((prev) => {
        if (!isMultiSelect) return [value];
        return prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      });
    },
    [isMultiSelect]
  );

  const isDate = activeQuestion?.questionType === 'date';

  // Validate the date and reflect the result into `dateError`: a message on
  // failure, or null to clear. Two distinct failures get distinct copy — a
  // `null` normalizedDate means the input couldn't be parsed (blank / incomplete
  // / not a real date), so the "on or after {min}" floor message would be
  // misleading; only a parsed-but-too-old date earns it. The min comes straight
  // off the validation result, so it lives only in the message.
  const validateDate = useCallback(() => {
    const { isValid, min, normalizedDate } = validateSurveyDate(dateValue);
    if (!isValid) {
      setDateError(
        normalizedDate === null
          ? t('Enter a valid date (MM/DD/YYYY).')
          : t('Enter a date on or after {date}.', { date: formatBirthdayForDisplay(min) })
      );
      return null;
    }
    setDateError(null);
    return normalizedDate;
  }, [dateValue, t]);

  // Surface the error as soon as the field loses focus (not just on Continue).
  // Blur only fires after the field's been focused, so an empty value here means
  // the user left it blank — flag that too (validateSurveyDate('') is invalid).
  const onDateBlur = useCallback(() => {
    validateDate();
  }, [validateDate]);

  // Explicitly `false` → skippable. Absent defaults to required, so the legacy
  // screeners (which never set the flag) keep their behavior.
  const isOptional = activeQuestion?.isRequired === false;

  const onContinuePress = useCallback(() => {
    if (!activeQuestion) return;
    if (isDate) {
      // An optional date left blank is a skip; anything typed still validates.
      if (isOptional && dateValue.trim() === '') {
        commitAnswer('');
        return;
      }
      const normalizedDate = validateDate();
      if (normalizedDate) commitAnswer(normalizedDate);
      return;
    }
    if (selected.length === 0) {
      // Skipping an optional question still commits the key (an empty array for
      // multi-select, '' for single) rather than omitting it — the
      // dynamic-assessment server registers the element name as an
      // allowed_signal_key and rejects an empty answers map (same reason the
      // gate auto-advance sends `{ _gate: '' }`).
      if (isOptional) {
        commitAnswer(isMultiSelect ? [] : '');
        return;
      }
      setShowRequiredError(true);
      return;
    }
    // Multi-select submits the full set as an array; single-select submits the
    // one chosen value as a string.
    commitAnswer(isMultiSelect ? selected : (selected[0] ?? ''));
  }, [
    activeQuestion,
    isDate,
    isOptional,
    isMultiSelect,
    dateValue,
    validateDate,
    selected,
    commitAnswer,
  ]);

  if (!activeQuestion) return null;

  return (
    <Screen
      busy={isSubmitting}
      footer={
        <CtaButton
          isBusy={isSubmitting}
          isDisabled={isSubmitting}
          onPress={onContinuePress}
          testID={testIDPrefix ? `${testIDPrefix}-continue` : undefined}
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={activeQuestion.text ?? ''}
          subtitle={activeQuestion.additionalInfo ?? undefined}
        />

        {showRequiredError ? (
          <Text className='text-error-700'>
            <T _str='Please select an answer to continue.' />
          </Text>
        ) : null}

        {isDate ? (
          <TextField
            value={dateValue}
            onChangeText={(text) => {
              setDateValue(text);
              setDateError(null);
            }}
            onBlur={onDateBlur}
            format={formatBirthdayForDisplay}
            placeholder={t('MM / DD / YYYY')}
            inputMode='numeric'
            keyboardType='number-pad'
            errorMessage={dateError ?? undefined}
            testID={testIDPrefix ? `${testIDPrefix}-input` : undefined}
          />
        ) : (
          <VStack space='lg'>
            {activeQuestion.choices.map((choice) => {
              const isSelected = selected.includes(choice.value);
              return (
                <SelectableRow
                  key={choice.value}
                  isSelected={isSelected}
                  selectionRole={isMultiSelect ? 'checkbox' : 'radio'}
                  onPress={() => toggleChoice(choice.value)}
                  accessibilityLabel={choice.text}
                  // Checkbox rows (label-only) get the taller HealthConditions
                  // padding; avatar rows already grow to fit the `lg` icon, so
                  // extra padding there would make them too tall.
                  className={isMultiSelect ? 'py-6' : undefined}
                  testID={testIDPrefix ? `${testIDPrefix}-${slugify(choice.text)}` : undefined}
                >
                  {isMultiSelect ? (
                    <Checkbox value={choice.value} isChecked={isSelected} pointerEvents='none'>
                      <CheckboxCheckIndicator />
                    </Checkbox>
                  ) : null}
                  {choice.imageLink ? (
                    <Avatar size='lg' className='bg-[#FDF6EE]'>
                      <AvatarImage source={{ uri: choice.imageLink }} alt={choice.text} />
                    </Avatar>
                  ) : null}
                  <Text size='md' weight='semibold' className='flex-1 text-typography-900'>
                    {choice.text}
                  </Text>
                </SelectableRow>
              );
            })}
          </VStack>
        )}

        <ContactSupport showPhone />
      </VStack>
    </Screen>
  );
}
